import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Box, Flex, Tabs as PillTab, TabsContainer, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Input } from 'components/Input/Input'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { ChipsContainer } from 'components/CheckBox/CheckBox.styled'
import { CircleButton } from 'pages/Shifts/Shifts.styled'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { ReorganizeIcon } from 'assets/icons/ReorganizeIcon'
import { IConfigurationTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { IFoodKitchen, IKitchenStandby } from 'types/foodLogistics/foodKitchen.interface'
import { IMealWindow } from 'types/foodLogistics/mealWindow.interface'
import { CreateKitchenModal } from '../components/CreateKitchenModal'
import { FOOD_DIETARY_TAGS, FOOD_MEAL_PRIORITY, FOOD_UNKNOWN_POLICIES } from '../foodData'

const CONFIG_SUB_TABS = ['Meal Windows', 'Policy', 'Kitchens', 'Standby']

export const ConfigurationTab = ({ kitchens, setKitchens, windows, onToolbarChange }: IConfigurationTabProps) => {
  const [tab, setTab] = useState<string>(CONFIG_SUB_TABS[0])
  const [createKitchenOpen, setCreateKitchenOpen] = useState(false)

  const save = (what: string) => toast.success(`Configuration saved as version cfg-4 (${what}) — effective 21/2/2028`)

  useEffect(() => {
    onToolbarChange?.(
      <Flex gap='1rem' alignEnd wrap>
        <TabsContainer>
          {CONFIG_SUB_TABS.map((t) => (
            <PillTab key={t} active={tab === t} onClick={() => setTab(t)}>
              {t}
            </PillTab>
          ))}
        </TabsContainer>
        {tab === 'Kitchens' && (
          <Button variant='primary' onClick={() => setCreateKitchenOpen(true)}>
            Create Kitchen
          </Button>
        )}
      </Flex>,
    )
    return () => onToolbarChange?.(null)
  }, [tab, onToolbarChange])

  return (
    <Flex column gap='1rem' width='100%'>
      {tab === 'Meal Windows' && <MealWindowsConfig windows={windows} onSave={() => save('meal windows')} />}
      {tab === 'Policy' && <PolicyConfig onSave={() => save('policy')} />}
      {tab === 'Kitchens' && (
        <KitchensConfig kitchens={kitchens} setKitchens={setKitchens} onSave={() => save('kitchens')} />
      )}
      {tab === 'Standby' && <StandbyConfig kitchens={kitchens} setKitchens={setKitchens} />}
      {createKitchenOpen && (
        <CreateKitchenModal
          existingKitchens={kitchens}
          closeModal={() => setCreateKitchenOpen(false)}
          onCreate={(kitchen) => {
            setKitchens((ks) => [...ks, kitchen])
            setCreateKitchenOpen(false)
            toast.success(`Kitchen "${kitchen.name}" created — effective 21/2/2028`)
          }}
        />
      )}
    </Flex>
  )
}

const MealWindowsConfig = ({ windows, onSave }: { windows: IMealWindow[]; onSave: () => void }) => (
  <Container>
    <ContainerHeader>
      <TitleText weight='700'>Meal Windows</TitleText>
    </ContainerHeader>
    <ContainerContent gap='0.625rem'>
      {windows.map((w) => (
        <Card key={w.id}>
          <CardBody>
            <BodyText weight='700'>{w.name}</BodyText>
            <Flex gap='0.75rem' mt='8' width='100%'>
              <Input width='100%' labelText='Service window' placeholder='e.g. 06:30–08:00' defaultValue={w.time} nomarg />
              <Input width='100%' labelText='Cut-off' placeholder='e.g. 20:30 (prev. day)' defaultValue={w.cutoff} nomarg />
              <Input width='100%' labelText='Default kitchen' placeholder='Auto' defaultValue={w.id === 4 ? 'Main Kitchen' : 'Auto'} nomarg />
            </Flex>
          </CardBody>
        </Card>
      ))}
      <Flex gap='0.5rem' mt='8' width='100%' between>
        <Button variant='secondary'>Add</Button>
        <Button variant='primary' onClick={onSave}>
          Save
        </Button>
      </Flex>
    </ContainerContent>
  </Container>
)

const PolicyConfig = ({ onSave }: { onSave: () => void }) => {
  const [maxMeals, setMaxMeals] = useState('2')
  const [priorities, setPriorities] = useState<string[]>(FOOD_MEAL_PRIORITY)
  const [unknownPolicy, setUnknownPolicy] = useState('NO_MEAL')
  const [cancelNoShow, setCancelNoShow] = useState(true)
  const [showRate, setShowRate] = useState(true)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const from = priorities.findIndex((p) => p === active.id)
      const to = priorities.findIndex((p) => p === over.id)
      setPriorities(arrayMove(priorities, from, to))
    }
  }

  return (
    <Container>
      <ContainerHeader>
        <TitleText weight='700'>Site Policy</TitleText>
      </ContainerHeader>
      <ContainerContent gap='1rem'>
        <Input
          type='number'
          labelText='Max meals per operational day'
          placeholder='e.g. 2'
          value={maxMeals}
          onChange={(e: any) => setMaxMeals(e.target.value)}
          nomarg
          width='20rem'
        />
        <BodyText weight='600'>Meal selection priority</BodyText>
        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext items={priorities} strategy={verticalListSortingStrategy}>
            {priorities.map((m, i) => (
              <SortablePriorityItem key={m} id={m} index={i + 1} label={m} />
            ))}
          </SortableContext>
        </DndContext>
        <BodyText weight='600'>UNKNOWN presence policy</BodyText>
        {FOOD_UNKNOWN_POLICIES.map((p) => (
          <Card
            key={p.key}
            onClick={() => setUnknownPolicy(p.key)}
            fullBorder={unknownPolicy === p.key ? colors.orange : undefined}
            hoverBorder
            noScale
          >
            <CardBody>
              <Flex gap='0.75rem' center>
                <CircleButton checked={unknownPolicy === p.key} />
                <Flex column gap='0.125rem'>
                  <BodyText weight='700'>{p.label}</BodyText>
                  <OtherText grey>{p.desc}</OtherText>
                </Flex>
              </Flex>
            </CardBody>
          </Card>
        ))}
        <BodyText weight='600'>Attendance reconciliation</BodyText>
        <CheckBox
          square
          state={cancelNoShow}
          onClick={() => setCancelNoShow(!cancelNoShow)}
          title='Cancel intent pre-cutoff on attendance no-show'
        />
        <CheckBox
          square
          state={showRate}
          onClick={() => setShowRate(!showRate)}
          title='Apply per-crew show-rate coefficient (trailing 14 days)'
        />
        <Flex>
          <Button variant='primary' onClick={onSave}>
            Save
          </Button>
        </Flex>
      </ContainerContent>
    </Container>
  )
}

const operatorOptions = [
  { label: 'Internal', value: 'Internal' },
  { label: 'Contractor', value: 'Contractor' },
]

const KitchensConfig = ({
  kitchens,
  setKitchens,
  onSave,
}: {
  kitchens: IFoodKitchen[]
  setKitchens: React.Dispatch<React.SetStateAction<IFoodKitchen[]>>
  onSave: () => void
}) => {
  const toggleCap = (kitchenId: number, tag: string) => {
    setKitchens((ks) =>
      ks.map((x) =>
        x.id === kitchenId
          ? { ...x, caps: x.caps.includes(tag) ? x.caps.filter((c) => c !== tag) : [...x.caps, tag] }
          : x,
      ),
    )
  }

  const togglePackout = (kitchenId: number) => {
    setKitchens((ks) => ks.map((x) => (x.id === kitchenId ? { ...x, packout: !x.packout } : x)))
  }

  const updateSoftCap = (kitchenId: number, value: number) => {
    setKitchens((ks) => ks.map((x) => (x.id === kitchenId ? { ...x, softCap: value } : x)))
  }

  const updateOperator = (kitchenId: number, value: 'Internal' | 'Contractor') => {
    setKitchens((ks) => ks.map((x) => (x.id === kitchenId ? { ...x, operator: value } : x)))
  }

  return (
    <Box>
      <Flex column gap='0.625rem' width='100%'>
        {kitchens.map((k) => (
          <Card key={k.id}>
            <CardBody>
              <Flex column gap='0.25rem'>
                <BodyText weight='700'>{k.name}</BodyText>
                <OtherText grey>Dietary capability tag set</OtherText>
              </Flex>
              <ChipsContainer style={{ marginTop: '0.75rem' }}>
                {FOOD_DIETARY_TAGS.map((t) => (
                  <CheckChip key={t} text={t} active={k.caps.includes(t)} onClick={() => toggleCap(k.id, t)} />
                ))}
              </ChipsContainer>
              <Flex gap='1rem' alignEnd mt='16' wrap>
                <CheckBox state={k.packout} onClick={() => togglePackout(k.id)} square title='Packout support' />
                <Input
                  type='number'
                  labelText='Soft capacity'
                  placeholder='e.g. 120'
                  value={k.softCap}
                  onChange={(e: any) => updateSoftCap(k.id, Number(e.target.value))}
                  width='8rem'
                />
                <HonestSelectInput
                  labelText='Operator'
                  options={operatorOptions}
                  defaultValue={k.operator || 'Internal'}
                  onChange={(v: any) => updateOperator(k.id, v.value)}
                  name='operator'
                  width='10rem'
                />
              </Flex>
            </CardBody>
          </Card>
        ))}
        <Flex>
          <Button variant='primary' onClick={onSave}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Box>
  )
}

const StandbyConfig = ({
  kitchens,
  setKitchens,
}: {
  kitchens: IFoodKitchen[]
  setKitchens: React.Dispatch<React.SetStateAction<IFoodKitchen[]>>
}) => {
  const [replQty, setReplQty] = useState<Record<string, string>>({})

  const standbyColumns = (kitchen: IFoodKitchen): IHonestTableColumn<IKitchenStandby & { id: string }>[] => [
    {
      id: 'tags',
      header: 'Tag set',
      cell: (s) => (
        <Flex wrap gap='0.25rem'>
          {s.tags.map((t) => (
            <StatusPill key={t} status='accent'>{t}</StatusPill>
          ))}
        </Flex>
      ),
    },
    {
      id: 'qty',
      header: 'Quantity on hand',
      cell: (s) => (
        <BodyText color={s.qty <= s.min ? colors.red : undefined} weight='700'>
          {s.qty}
          {s.qty <= s.min && ' below threshold'}
        </BodyText>
      ),
    },
    { id: 'min', header: 'Min threshold', cell: (s) => <BodyText>{s.min}</BodyText> },
    {
      id: 'replenish',
      header: 'Replenish',
      cell: (s) => {
        const key = s.id
        return (
          <Flex gap='0.5rem' center>
            <Input
              type='number'
              placeholder='Quantity'
              value={replQty[key] || ''}
              onChange={(e: any) => setReplQty({ ...replQty, [key]: e.target.value })}
              width='6rem'
            />
            <Button
              variant='secondary'
              size='sm'
              disabled={!replQty[key] || Number(replQty[key]) < 1}
              onClick={() => {
                const n = Number(replQty[key])
                const idx = Number(key.split('-')[1])
                setKitchens((ks) =>
                  ks.map((x) =>
                    x.id === kitchen.id
                      ? { ...x, standby: x.standby.map((y, j) => (j === idx ? { ...y, qty: y.qty + n } : y)) }
                      : x,
                  ),
                )
                toast.success(`Replenished +${n} ${s.tags.join('+')} at ${kitchen.name} (audited)`)
                setReplQty({ ...replQty, [key]: '' })
              }}
            >
              Add
            </Button>
          </Flex>
        )
      },
    },
  ]

  const toggleStandby = (kitchenId: number) => {
    setKitchens((ks) => ks.map((x) => (x.id === kitchenId ? { ...x, standbyEnabled: !x.standbyEnabled } : x)))
  }

  return (
    <Flex column gap='1rem' width='100%'>
      {kitchens.map((k) => {
        const data = k.standby.map((s, i) => ({ ...s, id: `${k.id}-${i}` }))
        return (
          <Container key={k.id}>
            <ContainerHeader padding>
              <Flex between center width='100%'>
                <TitleText weight='700'>{k.name}</TitleText>
                <CheckBox
                  square
                  state={k.standbyEnabled}
                  onClick={() => {
                    toggleStandby(k.id)
                    toast.warn(
                      k.standbyEnabled
                        ? `WARNING: disabling standby removes the ONLY buffer at ${k.name}`
                        : `Standby enabled at ${k.name}`,
                    )
                  }}
                  title='Enabled'
                />
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              <HonestTable columns={standbyColumns(k)} data={data} keyField='id' />
            </ContainerContent>
          </Container>
        )
      })}
    </Flex>
  )
}

const SortablePriorityItem = ({ id, index, label }: { id: string; index: number; label: string }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id })
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card>
        <CardBody>
          <Flex gap='0.75rem' center>
            <div {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex' }}>
              <ReorganizeIcon />
            </div>
            <BodyText>
              {index} · {label}
            </BodyText>
          </Flex>
        </CardBody>
      </Card>
    </div>
  )
}
